import { Component, inject, signal, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';

import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';

import { MenubarModule } from '@openng/optimus-ui/menubar';
import { MenuModule } from '@openng/optimus-ui/menu';
import { ButtonModule } from '@openng/optimus-ui/button';
import { Select } from '@openng/optimus-ui/select';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { TieredMenuModule } from '@openng/optimus-ui/tieredmenu';
import { DrawerModule } from '@openng/optimus-ui/drawer';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { MenuItem, MessageService } from '@openng/optimus-ui/api';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';

import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { AuthenticationMutations } from 'src/app/graphql/mutations/authentication.mutation';
import { menubarHeaderDesignToken } from 'src/app/theme/menubar-header.design-token';
import { ThemeService } from 'src/app/services/theme.service';
import { ColorblindService } from 'src/app/services/colorblind.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { EnvironmentApiService } from 'src/app/services/api/environment.service';
import { isTreoEnvironment } from 'src/app/utils/environment.utils';
import { AuthService } from 'src/app/services/auth.service';
import { LaunchdarklyService } from 'src/app/services/launchdarkly.service';
import { Theme } from 'src/app/enums/theme.enum';
import { TreoEnvironmentService, TREO_ENVIRONMENT_OPTIONS } from 'src/app/services/treo-environment.service';
import { menuDesignToken } from 'src/app/theme/menu';
import { drawerDesignToken } from 'src/app/theme/drawer';
import { environment, menuUrls } from 'src/environments/environment';
import { Environment } from 'src/app/models/navigation-environment.model';

/**
 * Main header of the application.
 */
@Component({
	selector: 'app-navigation-header',
	imports: [MenubarModule, MenuModule, ButtonModule, AsyncPipe, Select, FormsModule, TooltipModule, TieredMenuModule, DrawerModule, ToggleSwitchModule, ProgressSpinnerModule, CheckboxModule],
	templateUrl: './navigation-header.component.html',
	styleUrl: './navigation-header.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationHeaderComponent {
	public menubarHeaderDesignToken = menubarHeaderDesignToken;

	public menuDesignToken = menuDesignToken;

	public drawerDesignToken = drawerDesignToken;

	public themeService = inject(ThemeService);

	public colorblindService = inject(ColorblindService);

	private environmentService = inject(EnvironmentService);

	private environmentApiService = inject(EnvironmentApiService);

	public authService = inject(AuthService);

	private launchdarklyService = inject(LaunchdarklyService);

	private apolloClientService = inject(ApolloClientService);

	private destroyRef = inject(DestroyRef);

	private cdr = inject(ChangeDetectorRef);

	private breakpointObserver = inject(BreakpointObserver);

	private router = inject(Router);

	private messageService = inject(MessageService);

	private treoEnvironmentService = inject(TreoEnvironmentService);

	public Theme = Theme;

	public homeUrl = menuUrls.homeUrl;

	// TREO sub-environment filter (ILAB3 / ILAB3 Dev / ...), shown in its own hamburger-menu drawer for
	// the treo environment. Decides which sub-environments' runs appear in the dashboard/search/history.
	public treoEnvironmentOptions = TREO_ENVIRONMENT_OPTIONS;

	public selectedTreoEnvironments$ = this.treoEnvironmentService.selected$;

	/**
	 * Toggles one TREO sub-environment in the persisted filter (drives the test-history query).
	 * @param value
	 */
	public toggleTreoEnvironment(value: string): void {
		this.treoEnvironmentService.toggle(value);
	}

	public breakpoint = `${environment.breakPoint.toString()}px`;

	public isMobile$ = this.breakpointObserver.observe([`(max-width: ${this.breakpoint})`]).pipe(
		map(result => result.matches),
		distinctUntilChanged(),
	);

	public showUserDetails: boolean = false;

	public showPrivacyNotice = false;

	public showTreoEnvironments = false;

	/**
	 * Shared analytics transparency-notice text, sourced from the Fenix Web Analytics SDK
	 * (window.AnalyticsSDK.NOTICE) so every app shows identical wording. Informational only —
	 * no consent is collected. Undefined if the SDK has not loaded.
	 */
	public get privacyNotice() {
		return window.AnalyticsSDK?.NOTICE;
	}

	// Environment selector - populated dynamically from user's functional groups
	environments = signal<Environment[]>([]);

	// Selected environment synced with EnvironmentService
	// Getter/setter pattern is required for Optimus UI two-way binding [(ngModel)] to work with EnvironmentService sync
	private _selectedEnvironment = signal<Environment | null>(null);

	private _isInitialLoad = true; // Track if this is the first environment set

	public get selectedEnvironment(): Environment | null {
		return this._selectedEnvironment();
	}

	public set selectedEnvironment(value: Environment | null) {
		const previousEnv = this._selectedEnvironment()?.value;
		this._selectedEnvironment.set(value);

		if (value?.value) {
			this.environmentService.setEnvironment(value.value);

			// Show toast notification only if environment actually changed and not on initial load
			if (!this._isInitialLoad && previousEnv && previousEnv !== value.value) {
				this.messageService.add({
					severity: 'info',
					summary: 'Environment Changed',
					detail: `Environment has been changed to: ${value.value}`,
					life: 3000,
				});
			}

			// After first set, no longer initial load
			this._isInitialLoad = false;
		}
	}

	// Navigation menu items
	navigationItems = signal<MenuItem[]>([
		{
			label: 'Dashboard',
			icon: 'pi pi-home',
			command: () => {
				void this.router.navigate(['/']);
				if (!this.environmentService.getCurrentEnvironment()) {
					this.environmentService.requestShowWarningDialog();
				}
			},
		},
		{
			label: 'Analysis',
			icon: 'pi pi-chart-bar',
			visible: false,
			command: () => {
				void this.router.navigate(['/analysis']);
			},
		},
		{
			label: 'AI Conversation',
			icon: 'pi pi-comments',
			visible: false,
			command: () => {
				void this.router.navigate(['/ai-conversation']);
			},
		},
	]);

	// Hamburger menu items. Depends on the environment stream too so the TREO-only "Treo Environments"
	// item re-evaluates its visibility when the user switches environment (isTreoEnv reads a plain
	// field, so the menu must be rebuilt to pick up the change).
	public menuItems$: Observable<MenuItem[]> = combineLatest({
		user: this.authService.userData$,
		env: this.environmentService.environment$,
	}).pipe(
		map(({ user, env }) => [
			{
				label: user.firstName + ' ' + user.lastName,
				icon: 'pi pi-user',
				visible: !!user.sessionID,
				command: () => {
					this.showUserDetails = true;
				},
			},
			{
				label: 'Settings',
				icon: 'pi pi-cog',
				command: event => {
					this.navigateToSettings(event.originalEvent);
				},
			},
			{
				label: 'Treo Environments',
				icon: 'pi pi-globe',
				visible: isTreoEnvironment(env),
				command: () => {
					this.showTreoEnvironments = true;
				},
			},
			{
				icon: 'pi pi-question-circle',
				label: 'Support',
				command: () => {
					window.open(menuUrls.supportUrl, '_blank');
				},
			},
			/* TODO: Our wiki is empty */
			/* {
				icon: 'pi pi-book',
				label: 'Wiki',
				command: () => {
					window.open(menuUrls.wikiUrl, '_blank');
				},
			}, */
			{
				icon: 'pi pi-megaphone',
				label: 'Blog & News',
				visible: !environment.IsHero,
				command: () => {
					window.open(menuUrls.blogAndNewsUrl, '_blank');
				},
			},
			{
				icon: 'pi pi-home',
				label: 'Test Tools',
				command: () => {
					window.open(menuUrls.homeUrl, '_blank');
				},
			},
			{
				icon: 'pi pi-shield',
				label: 'Privacy & Data',
				command: () => {
					this.showPrivacyNotice = true;
				},
			},
			{
				label: user.sessionID ? 'Sign out' : 'Sign in',
				icon: user.sessionID ? 'pi pi-sign-out' : 'pi pi-sign-in',
				command: () => {
					if (user.sessionID) {
						this.signOut();
					} else {
						this.authService.login();
					}
				},
			},
		]),
	);

	constructor() {
		this.themeService.getTheme();
		this.colorblindService.getColorblindMode();

		this.authService.userData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(userData => {
			if (userData.isLoggedIn && userData.functionalGroups && userData.functionalGroups.length > 0) {
				this.loadEnvironmentsForFunctionalGroups(userData.functionalGroups);
			}
		});

		this.environmentService.environment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(env => {
			this.setEnvironmentMenuItemsVisible(!!env);
			this.syncDropdownToEnvironment(env);
		});
	}

	/**
	 * Reflects the selected environment (as held by EnvironmentService) in the dropdown. This keeps the
	 * dropdown in sync when the environment is changed from outside the header — notably when a shared
	 * link's ?env= is adopted by the urlEnvironmentGuard.
	 * @param env The selected environment value, or null when cleared
	 */
	private syncDropdownToEnvironment(env: string | null): void {
		if (!env) {
			this._selectedEnvironment.set(null);
			this.cdr.markForCheck();
			return;
		}
		if (this._selectedEnvironment()?.value.toLowerCase() === env.toLowerCase()) {
			return;
		}
		const match = this.environments().find(e => e.value.toLowerCase() === env.toLowerCase());
		if (!match) {
			return;
		}
		this._selectedEnvironment.set(match);
		this._isInitialLoad = false;
		this.cdr.markForCheck();
	}

	private setEnvironmentMenuItemsVisible(visible: boolean): void {
		this.navigationItems.update(items => items.map((item, index) => (index === 0 ? item : { ...item, visible })));
	}

	/**
	 * Fetches environments for all functional groups and appends them to the environments list.
	 * Removes duplicates based on environment value.
	 */
	private static readonly RESULTS_ENV = 'results';

	// TODO: Undo tthe changes in this commit when results is released
	private static readonly RESULTS_FLAG = 'export-results-to-analyzer';

	private loadEnvironmentsForFunctionalGroups(functionalGroups: string[]): void {
		const capitalizedGroups = functionalGroups.map(groupUid => groupUid.toUpperCase());
		const requests = capitalizedGroups.map(groupUid => this.environmentApiService.getEnvironmentsByFunctionalGroup$(groupUid));

		forkJoin(requests)
			.pipe(
				switchMap(results =>
					this.launchdarklyService.launchDarklyFlags$.pipe(
						take(1),
						map(flags => ({ results, resultsEnabled: !!flags[NavigationHeaderComponent.RESULTS_FLAG] })),
					),
				),
				take(1),
			)
			.subscribe({
				next: ({ results, resultsEnabled }) => {
					let allEnvironments = results.flat();

					// Filter out "results" entirely if user doesn't have the feature flag
					if (!resultsEnabled) {
						allEnvironments = allEnvironments.filter(env => env !== NavigationHeaderComponent.RESULTS_ENV);
					}

					const newEnvObjects: Environment[] = allEnvironments.map(env => ({
						label: env,
						value: env,
					}));

					const uniqueNewEnvs = newEnvObjects.filter(newEnv => !this.environments().some(existingEnv => existingEnv.value === newEnv.value));

					this.environments.update(envs => [...envs, ...uniqueNewEnvs]);

					// Include "results" only if user has the feature flag enabled
					if (resultsEnabled) {
						const hasNonAnalyzerFg = results.some(envList => envList.length === 0);
						if (hasNonAnalyzerFg || this.environments().length === 0) {
							if (!this.environments().some(e => e.value === NavigationHeaderComponent.RESULTS_ENV)) {
								this.environments.update(envs => [...envs, { label: NavigationHeaderComponent.RESULTS_ENV, value: NavigationHeaderComponent.RESULTS_ENV }]);
							}
						}
					}

					// Publish the resolved list so route guards / other components can run access checks
					// without the header being present.
					this.environmentService.setAvailableEnvironments(this.environments().map(e => e.value));

					const currentEnv = this.environmentService.getCurrentEnvironment();
					const envObject = this.environments().find(e => e.value.toLowerCase() === currentEnv.toLowerCase());

					if (envObject) {
						this._selectedEnvironment.set(envObject);
					} else if (!this._selectedEnvironment() && this.environments().length > 0) {
						// Default to the user's own analyzer environment if they have one, otherwise "results"
						const ownEnv = this.environments().find(e => e.value !== NavigationHeaderComponent.RESULTS_ENV);
						//const defaultEnv = ownEnv ?? this.environments().find(e => e.value === NavigationHeaderComponent.RESULTS_ENV)!;
						const defaultEnv = ownEnv ?? this.environments()[0];
						this._selectedEnvironment.set(defaultEnv);
						this.environmentService.setEnvironment(defaultEnv.value);
					}

					this.cdr.markForCheck();
					this.environmentService.markEnvironmentLoaded();
				},
				error: (error: any) => {
					console.error('[NavigationHeaderComponent] Error fetching environments:', error);
					this.environmentService.markEnvironmentLoaded();
				},
			});
	}

	/**
	 * Sign out user
	 */
	public signOut(): void {
		this.apolloClientService.authLayerClient
			.pipe(
				take(1),
				switchMap(client =>
					client.mutate<{ signOut: boolean | null }>({
						mutation: AuthenticationMutations.signOut,
					}),
				),
				map(response => response ?? false),
				tap(response => {
					if (response) {
						this.messageService.add({
							severity: 'success',
							detail: 'Successfully signed out',
						});
						this.authService.clearSession();
					} else {
						throw new Error('Failed when signing out');
					}
				}),
				catchError((error: any) => {
					this.messageService.add({
						severity: 'error',
						summary: 'Error when signing out',
						detail: error,
					});
					console.error('Error signing out:', error);
					this.authService.clearSession();
					return of(undefined);
				}),
				take(1),
			)
			.subscribe();
	}

	/**
	 * Navigates to the Dashboard page.
	 */
	public navigateToDashboard(): void {
		void this.router.navigate(['/']);
	}

	/**
	 * Navigates to the Settings page. When the user ctrl/cmd-clicks the menu item, the page is
	 * opened in a new browser tab instead (matching the behaviour of the external menu links).
	 * @param originalEvent The DOM event behind the menu item command, if any.
	 */
	public navigateToSettings(originalEvent?: Event): void {
		const mouseEvent = originalEvent as MouseEvent | undefined;
		if (mouseEvent?.ctrlKey || mouseEvent?.metaKey) {
			const url = this.router.serializeUrl(this.router.createUrlTree(['/settings']));
			window.open(url, '_blank');
			return;
		}
		void this.router.navigate(['/settings']);
	}
}
